import React, { useCallback, useMemo, useState } from 'react'
import { Popover, Typography } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { Icon } from '@iconify/react'
import { MilestoneDetail } from '../../../../../../../../domain/entity/MilestoneEntity'
import { colorPalette } from '../../../../../../../style/colorPalette'
import BigFireIcon from '../../../../../../../components/icons/BigFireIcon'

export type MilestoneStatus = 'achieved' | 'overdue' | 'pending'

export type MilestoneWithPosition = {
  milestone: MilestoneDetail
  position?: number
}

const styles = {
  badge: {
    position: 'absolute' as const,
    top: -3,
    right: -5,
    fontSize: 7,
    fontWeight: 'bold',
    color: colorPalette.monotone[10],
    backgroundColor: colorPalette.monotone[1],
    borderRadius: '50%',
    width: 9,
    height: 9,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative' as const,
    lineHeight: 1,
  },
  popoverContent: {
    padding: '8px 12px',
  },
  popoverItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
} as const

const popoverProps = {
  anchorOrigin: { vertical: 'bottom' as const, horizontal: 'left' as const },
  transformOrigin: { vertical: 'top' as const, horizontal: 'left' as const },
  sx: { transform: 'translateX(-6px)' },
}

export const getMilestoneStatus = (
  milestone: MilestoneDetail
): MilestoneStatus => {
  if (milestone.achieved) return 'achieved'
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const milestoneDate = new Date(milestone.milestoneDate)
  if (milestoneDate < today) return 'overdue'
  return 'pending'
}

const formatMilestoneDate = (dateString: string): string => {
  const date = new Date(dateString)
  return `${date.getMonth() + 1}/${date.getDate()}`
}

const StatusIcon = ({ milestone }: { milestone: MilestoneDetail }) => {
  const status = getMilestoneStatus(milestone)
  const baseStyle = { fontSize: 14, flexShrink: 0 }

  if (status === 'achieved') {
    return (
      <CheckCircleIcon sx={{ ...baseStyle, color: colorPalette.green[5] }} />
    )
  }
  if (status === 'overdue') {
    return <BigFireIcon style={baseStyle} />
  }
  return (
    <CheckCircleOutlineIcon
      sx={{ ...baseStyle, color: colorPalette.monotone[4] }}
    />
  )
}

const MilestoneIconWithBadge = ({
  milestone,
  count,
  opacity,
}: {
  milestone: MilestoneDetail
  count: number
  opacity: number
}) => (
  <span style={{ ...styles.iconContainer, opacity }}>
    <Icon
      icon={milestone.iconName ?? 'mdi:cards-diamond'}
      width={12}
      height={12}
      color={milestone.color}
    />
    {count > 1 && <span style={styles.badge}>{count}</span>}
  </span>
)

const MilestonePopoverContent = ({
  milestones,
  showDate = false,
}: {
  milestones: MilestoneDetail[]
  showDate?: boolean
}) => (
  <div style={styles.popoverContent}>
    {milestones.map((milestone, index) => (
      <div
        key={milestone.uuid}
        style={{
          ...styles.popoverItem,
          marginTop: index > 0 ? 4 : 0,
        }}
      >
        <Icon
          icon={milestone.iconName ?? 'mdi:cards-diamond'}
          width={14}
          height={14}
          color={milestone.color}
        />
        {showDate && (
          <Typography
            variant="body2"
            style={{
              fontSize: 11,
              color: colorPalette.monotone[5],
              whiteSpace: 'nowrap',
            }}
          >
            {formatMilestoneDate(milestone.milestoneDate)}
          </Typography>
        )}
        <Typography
          variant="body2"
          style={{ fontSize: 12, flex: 1, minWidth: 0 }}
        >
          {milestone.name}
        </Typography>
        <StatusIcon milestone={milestone} />
      </div>
    ))}
  </div>
)

export const MilestoneIcons = ({
  milestones,
  positioned = false,
}: {
  milestones: MilestoneWithPosition[]
  positioned?: boolean
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const open = Boolean(anchorEl)

  const allMilestones = milestones.map(item => item.milestone)
  const firstMilestone = milestones[0].milestone
  const allAchieved = allMilestones.every(milestone => milestone.achieved)

  const groupedByPosition = useMemo(() => {
    if (!positioned) return []
    const groups = new Map<number, MilestoneDetail[]>()
    for (const { milestone, position } of milestones) {
      const pos = position ?? 50
      if (!groups.has(pos)) {
        groups.set(pos, [])
      }
      groups.get(pos)!.push(milestone)
    }
    return Array.from(groups.entries()).map(([pos, items]) => ({
      position: pos,
      milestones: items,
    }))
  }, [milestones, positioned])

  if (!positioned) {
    return (
      <>
        <span onClick={handleClick} style={{ cursor: 'pointer' }}>
          <MilestoneIconWithBadge
            milestone={firstMilestone}
            count={milestones.length}
            opacity={allAchieved ? 0.4 : 1}
          />
        </span>
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          {...popoverProps}
        >
          <MilestonePopoverContent milestones={allMilestones} />
        </Popover>
      </>
    )
  }

  return (
    <>
      {groupedByPosition.map(({ position, milestones: groupMilestones }) => {
        const groupFirstMilestone = groupMilestones[0]
        const groupAllAchieved = groupMilestones.every(
          milestone => milestone.achieved
        )
        return (
          <span
            key={`pos-${position}`}
            onClick={handleClick}
            style={{
              position: 'absolute',
              left: `${position}%`,
              transform: 'translateX(-50%)',
              cursor: 'pointer',
            }}
          >
            <MilestoneIconWithBadge
              milestone={groupFirstMilestone}
              count={groupMilestones.length}
              opacity={groupAllAchieved ? 0.4 : 1}
            />
          </span>
        )
      })}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        {...popoverProps}
      >
        <MilestonePopoverContent milestones={allMilestones} showDate={true} />
      </Popover>
    </>
  )
}
